/**
 * A community's courses (gh #4872 Decisions 25 and 30; Design → Courses): courses → modules →
 * lessons, held by the community's `CommunityDO`, authored by the community's admins (the owners
 * and admins of its org) and read by its members.
 *
 * CONTENT is data the admins author in the app (`/api/communities/:communityId/authoring/courses`),
 * also while the communities switch is off, so lessons are ready before members see them. A
 * community holds at most `COMMUNITY_COURSES_MAX` courses, each at most
 * `COMMUNITY_COURSE_MODULES_MAX` modules and `COMMUNITY_COURSE_LESSONS_MAX` lessons, and at most
 * `COMMUNITY_LESSONS_MAX` lessons in all. A course and a lesson start as drafts (a lesson's create
 * may publish it at once): members see a course once it is published, and in it only its published
 * lessons (a module shows while it holds one). A create carrying a `client_id` is idempotent. Courses, modules and lessons are ordered by `position` among their siblings: a new
 * one comes last, and an update's `position` moves it there, the others shifting around it. A
 * lesson moves to another module of its course with `module_id`. Nothing is deleted: a draft is
 * hidden by unpublishing it. A course reads whole, every lesson's text as written, in one request
 * (`GET …/authoring/courses/:courseId/export`).
 *
 * A LESSON has a title, a video (`video`: a provider and that provider's video id, read from the
 * URL its author pastes — YouTube is the one provider in this program, any other URL is refused),
 * a Markdown text (`body`), at most `COMMUNITY_LESSON_LINKS_MAX` links, optionally one of the
 * community's rooms for its discussion (`room_id`), and whether it needs a paid plan
 * (`requires_paid_plan`, which the clients show, except the iOS app, and nothing gates).
 *
 * PROGRESS is the reader's own, kept in their account: "Mark complete"
 * (`POST …/courses/:courseId/lessons/:lessonId/complete`) records the lesson once, and a
 * community's first completion of a lesson by a person counts in its admins' stats
 * (`CommunityStats.courses`), with no one's id. Leaving a community keeps a person's progress, so
 * joining again restores it. The community's destruction erases it for the people who are its
 * members then; a person who had left it, or an owner or admin of its org who never joined, keeps
 * rows nothing reads again until their account's deletion, which erases every row. A person holds
 * progress in at most `COMMUNITY_PROGRESS_COMMUNITIES_MAX` communities, those rows included.
 *
 * READING (the member surface: the communities and threads switches, an interactive session, an
 * active member or an owner or admin of the org): the published courses with the reader's progress
 * (`GET …/courses`); a course's outline, titles only, with the reader's progress and their next
 * lesson (`GET …/courses/:courseId`, `communityNextLessonId`); and a lesson, whole
 * (`GET …/courses/:courseId/lessons/:lessonId`), its text both as written and as the tree its
 * clients draw (`body_tree`, `community-lesson-tree.ts`), parsed once when the text is saved.
 *
 * THE COURSES TAB shows for a community once it has a published course, under the label its admins
 * gave it (`CommunityHead.courses`; the label is a community setting, `courses_label`).
 */

import { z } from 'zod';
import { uuidSchema } from './common';
import { COMMUNITIES_PER_PERSON_MAX } from './communities';
import type { CommunityLessonTree } from './community-lesson-tree';

// =============================================================================
// LIMITS
// =============================================================================

/** The courses one community holds at most, drafts included (the Caps table). */
export const COMMUNITY_COURSES_MAX = 20;
/** The modules one course holds at most (the Caps table). */
export const COMMUNITY_COURSE_MODULES_MAX = 20;
/** The lessons one course holds at most, drafts included (the Caps table). */
export const COMMUNITY_COURSE_LESSONS_MAX = 100;
/** The lessons one community holds at most, drafts included (the Caps table). */
export const COMMUNITY_LESSONS_MAX = 500;
/** The longest course, module or lesson title, in characters. */
export const COMMUNITY_COURSE_TITLE_MAX_LENGTH = 200;
/** The longest course description, in characters. */
export const COMMUNITY_COURSE_DESCRIPTION_MAX_LENGTH = 2_000;
/** The longest lesson text (Markdown), in characters (the Caps table). */
export const COMMUNITY_LESSON_BODY_MAX_LENGTH = 20_000;
/** The links one lesson holds at most (the Caps table). */
export const COMMUNITY_LESSON_LINKS_MAX = 10;
/** The longest URL a lesson's link or video takes, in characters. */
export const COMMUNITY_LESSON_URL_MAX_LENGTH = 2_000;
/**
 * The communities one person keeps progress in at most (the Caps table: 50 communities × 500
 * lessons): past it, a completion in yet another community is refused (409,
 * `COMMUNITY_LESSON_PROGRESS_REASON`).
 */
export const COMMUNITY_PROGRESS_COMMUNITIES_MAX = COMMUNITIES_PER_PERSON_MAX;

/** The `details.reason` of the 409 a course's creation gets when the community holds `COMMUNITY_COURSES_MAX`. */
export const COMMUNITY_COURSES_REASON = 'courses_per_community';
/** The `details.reason` of the 409 a module's creation gets when its course holds `COMMUNITY_COURSE_MODULES_MAX`. */
export const COMMUNITY_COURSE_MODULES_REASON = 'modules_per_course';
/** The `details.reason` of the 409 a lesson's creation gets when its course holds `COMMUNITY_COURSE_LESSONS_MAX`. */
export const COMMUNITY_COURSE_LESSONS_REASON = 'lessons_per_course';
/** The `details.reason` of the 409 a lesson's creation gets when the community holds `COMMUNITY_LESSONS_MAX`. */
export const COMMUNITY_LESSONS_REASON = 'lessons_per_community';
/** The `details.reason` of the 409 a completion gets past `COMMUNITY_PROGRESS_COMMUNITIES_MAX`. */
export const COMMUNITY_LESSON_PROGRESS_REASON = 'lesson_progress';

// =============================================================================
// VIDEO
// =============================================================================

/**
 * The video providers a lesson takes (Decision 25: YouTube only in this program). A lesson stores
 * its video as a provider and that provider's id, so another provider later needs no migration.
 */
export const COMMUNITY_LESSON_VIDEO_PROVIDERS = ['youtube'] as const;
export type CommunityLessonVideoProvider = (typeof COMMUNITY_LESSON_VIDEO_PROVIDERS)[number];

/** A lesson's video: its provider, and that provider's id for it. */
export interface CommunityLessonVideo {
  provider: CommunityLessonVideoProvider;
  id: string;
}

/** A YouTube video id: 11 characters of the URL-safe base64 alphabet. */
const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
/** The hosts a YouTube video URL is on; `youtu.be` takes the id as its whole path. */
const YOUTUBE_HOSTS = new Set(['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtube-nocookie.com', 'www.youtube-nocookie.com']);
const YOUTU_BE = 'youtu.be';
/** The paths that end in the id: `/embed/{id}`, `/shorts/{id}`, `/live/{id}`, `/v/{id}`. */
const YOUTUBE_ID_PATH = /^\/(?:embed|shorts|live|v)\/([A-Za-z0-9_-]{11})\/?$/;

/**
 * The video a URL names, by provider, or null for a URL no provider of
 * `COMMUNITY_LESSON_VIDEO_PROVIDERS` takes. YouTube's forms: `https://www.youtube.com/watch?v={id}`
 * (also on `m.` and with other parameters), `https://youtu.be/{id}`, and `/embed/`, `/shorts/`,
 * `/live/` and `/v/` paths (also on `youtube-nocookie.com`), over http or https. Parsed by
 * pattern, so it runs alike on every client.
 */
export function parseCommunityLessonVideoUrl(url: string): CommunityLessonVideo | null {
  const parts = /^https?:\/\/([^/?#]+)([^?#]*)(?:\?([^#]*))?(?:#.*)?$/i.exec(url.trim());
  if (!parts) return null;
  const host = parts[1]!.toLowerCase();
  const path = parts[2] ?? '';
  const query = parts[3] ?? '';
  let id: string | undefined;
  if (host === YOUTU_BE) {
    id = /^\/([A-Za-z0-9_-]{11})\/?$/.exec(path)?.[1];
  } else if (YOUTUBE_HOSTS.has(host)) {
    if (path === '/watch' || path === '/watch/') {
      id = query.split('&').find((param) => param.startsWith('v='))?.slice(2);
    } else {
      id = YOUTUBE_ID_PATH.exec(path)?.[1];
    }
  }
  return id !== undefined && YOUTUBE_ID.test(id) ? { provider: 'youtube', id } : null;
}

/** Where a client embeds `video`, by its provider: YouTube's privacy-enhanced player. */
export function communityLessonVideoEmbedUrl(video: CommunityLessonVideo): string {
  switch (video.provider) {
    case 'youtube':
      return `https://www.youtube-nocookie.com/embed/${video.id}`;
  }
}

/** Where a client opens `video` outside the app, by its provider: YouTube's own page, which its app opens. */
export function communityLessonVideoWatchUrl(video: CommunityLessonVideo): string {
  switch (video.provider) {
    case 'youtube':
      return `https://www.youtube.com/watch?v=${video.id}`;
  }
}

// =============================================================================
// REQUEST
// =============================================================================

/** A route on one course: `/api/communities/:communityId/{courses,authoring/courses}/:courseId/…`. */
export const communityCourseIdParam = z.object({ communityId: uuidSchema, courseId: uuidSchema });

/** A route on one module: `…/authoring/courses/:courseId/modules/:moduleId`. */
export const communityCourseModuleIdParam = z.object({ communityId: uuidSchema, courseId: uuidSchema, moduleId: uuidSchema });

/** A route on one lesson: `…/courses/:courseId/lessons/:lessonId/…`. */
export const communityLessonIdParam = z.object({ communityId: uuidSchema, courseId: uuidSchema, lessonId: uuidSchema });

/** A course's, a module's or a lesson's title: trimmed, not empty. */
export const communityCourseTitleSchema = z.string().trim().min(1).max(COMMUNITY_COURSE_TITLE_MAX_LENGTH);

/** A course's description: trimmed; empty for none. */
export const communityCourseDescriptionSchema = z.string().trim().max(COMMUNITY_COURSE_DESCRIPTION_MAX_LENGTH);

/** A place among siblings, from 0; one past the last places it last. */
export const communityCoursePositionSchema = z.number().int().min(0);

/**
 * A lesson's video URL, as its author pastes it: the video a provider of
 * `COMMUNITY_LESSON_VIDEO_PROVIDERS` takes from it (`parseCommunityLessonVideoUrl`); any other URL
 * is refused.
 */
export const communityLessonVideoUrlSchema = z
  .string()
  .trim()
  .max(COMMUNITY_LESSON_URL_MAX_LENGTH)
  .transform((url, ctx): CommunityLessonVideo => {
    const video = parseCommunityLessonVideoUrl(url);
    if (video) return video;
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Only a YouTube video URL is accepted' });
    return z.NEVER;
  });

/** A lesson's link: what it says, and an http or https URL (never `javascript:`). */
export const communityLessonLinkSchema = z
  .object({
    title: communityCourseTitleSchema,
    url: z
      .string()
      .trim()
      .max(COMMUNITY_LESSON_URL_MAX_LENGTH)
      .url()
      .refine((url) => /^https?:\/\//i.test(url), 'must be an http or https URL'),
  })
  .strict();

export const communityLessonLinksSchema = z.array(communityLessonLinkSchema).max(COMMUNITY_LESSON_LINKS_MAX);

/** A lesson's text, Markdown, as written: at most `COMMUNITY_LESSON_BODY_MAX_LENGTH` characters; empty for none. */
export const communityLessonBodySchema = z.string().max(COMMUNITY_LESSON_BODY_MAX_LENGTH);

/**
 * POST /api/communities/:communityId/authoring/courses — a new course, a draft, placed last.
 * `client_id`, on this and on a module's and a lesson's create, makes a retried create idempotent: a
 * create naming the `client_id` of one already made answers that one as it stands, wherever it is,
 * and makes none.
 */
export const communityCourseCreateBody = z
  .object({ title: communityCourseTitleSchema, description: communityCourseDescriptionSchema.optional(), client_id: uuidSchema.optional() })
  .strict();

export type CommunityCourseCreateBody = z.input<typeof communityCourseCreateBody>;

/** PATCH …/authoring/courses/:courseId — what to change: only what is given. */
export const communityCourseUpdateBody = z
  .object({
    title: communityCourseTitleSchema.optional(),
    description: communityCourseDescriptionSchema.optional(),
    position: communityCoursePositionSchema.optional(),
    published: z.boolean().optional(),
  })
  .strict()
  .refine((b) => Object.values(b).some((value) => value !== undefined), 'nothing to change');

export type CommunityCourseUpdateBody = z.input<typeof communityCourseUpdateBody>;

/** POST …/authoring/courses/:courseId/modules — a new module, placed last; `client_id` as a course's create. */
export const communityModuleCreateBody = z.object({ title: communityCourseTitleSchema, client_id: uuidSchema.optional() }).strict();

export type CommunityModuleCreateBody = z.input<typeof communityModuleCreateBody>;

/** PATCH …/authoring/courses/:courseId/modules/:moduleId — its new title or place. */
export const communityModuleUpdateBody = z
  .object({ title: communityCourseTitleSchema.optional(), position: communityCoursePositionSchema.optional() })
  .strict()
  .refine((b) => b.title !== undefined || b.position !== undefined, 'nothing to change');

export type CommunityModuleUpdateBody = z.input<typeof communityModuleUpdateBody>;

/**
 * POST …/authoring/courses/:courseId/lessons — a new lesson, placed last in `module_id` (a module of
 * this course): a draft, or published at once with `published`. `room_id` is one of the community's
 * rooms; `client_id` as a course's create.
 */
export const communityLessonCreateBody = z
  .object({
    module_id: uuidSchema,
    title: communityCourseTitleSchema,
    video_url: communityLessonVideoUrlSchema.nullable().optional(),
    body: communityLessonBodySchema.optional(),
    links: communityLessonLinksSchema.optional(),
    room_id: uuidSchema.nullable().optional(),
    requires_paid_plan: z.boolean().optional(),
    published: z.boolean().optional(),
    client_id: uuidSchema.optional(),
  })
  .strict();

export type CommunityLessonCreateBody = z.input<typeof communityLessonCreateBody>;

/**
 * PATCH …/authoring/courses/:courseId/lessons/:lessonId — what to change: only what is given.
 * `module_id` moves it to another module of its course (last there, or at `position`); null
 * `video_url` and `room_id` clear them.
 */
export const communityLessonUpdateBody = z
  .object({
    module_id: uuidSchema.optional(),
    title: communityCourseTitleSchema.optional(),
    video_url: communityLessonVideoUrlSchema.nullable().optional(),
    body: communityLessonBodySchema.optional(),
    links: communityLessonLinksSchema.optional(),
    room_id: uuidSchema.nullable().optional(),
    requires_paid_plan: z.boolean().optional(),
    position: communityCoursePositionSchema.optional(),
    published: z.boolean().optional(),
  })
  .strict()
  .refine((b) => Object.values(b).some((value) => value !== undefined), 'nothing to change');

export type CommunityLessonUpdateBody = z.input<typeof communityLessonUpdateBody>;

// =============================================================================
// RESPONSE: the member surface
// =============================================================================

/**
 * The Courses tab of a community (Decisions 25 and 30), on its head once it has a published
 * course. `label` is its name as the community's admins typed it, shown untranslated, or '' for the
 * client's locale default ("Courses"; pt and es "Cursos").
 */
export interface CommunityCoursesTab {
  label: string;
}

/** A published course as the Courses tab lists it, with the reader's progress in it. */
export interface CommunityCourseListing {
  course_id: string;
  title: string;
  /** '' for none. */
  description: string;
  /** Its published lessons. */
  lesson_count: number;
  /** Those the reader marked complete. */
  completed_count: number;
}

/** GET /api/communities/:communityId/courses — the published courses, by position (at most `COMMUNITY_COURSES_MAX`). */
export interface CommunityCoursesResponse {
  data: CommunityCourseListing[];
}

/** A lesson in a course's outline: its title only (the 32 MiB rule), and the reader's progress. */
export interface CommunityOutlineLesson {
  lesson_id: string;
  title: string;
  requires_paid_plan: boolean;
  /** When the reader marked it complete; null when they did not. */
  completed_at: string | null;
}

/** A module in a course's outline: its published lessons, by position. */
export interface CommunityOutlineModule {
  module_id: string;
  title: string;
  lessons: CommunityOutlineLesson[];
}

/**
 * A published course's outline: its modules holding a published lesson, by position, and the
 * reader's next lesson (`communityNextLessonId`): null once they completed its last one.
 */
export interface CommunityCourseOutline {
  course_id: string;
  title: string;
  description: string;
  modules: CommunityOutlineModule[];
  next_lesson_id: string | null;
}

/** GET /api/communities/:communityId/courses/:courseId */
export interface CommunityCourseOutlineResponse {
  data: CommunityCourseOutline;
}

/** A lesson's link. */
export interface CommunityLessonLink {
  title: string;
  url: string;
}

/** A lesson, whole, as its page shows it. */
export interface CommunityLesson {
  lesson_id: string;
  course_id: string;
  module_id: string;
  title: string;
  /** Its video; null for none. A client embeds `communityLessonVideoEmbedUrl` or opens `communityLessonVideoWatchUrl`. */
  video: CommunityLessonVideo | null;
  /** Markdown; '' for none. */
  body: string;
  /**
   * `body` parsed into the tree its clients draw (`community-lesson-tree.ts`): they draw this, and
   * never parse `body`. Every read and write of one lesson answers it; a course's export
   * (`CommunityLessonExport`) carries none.
   */
  body_tree: CommunityLessonTree;
  links: CommunityLessonLink[];
  /** Its discussion room, one of the community's rooms; null for none. */
  room_id: string | null;
  /** It needs a paid plan, which the clients say, except the iOS app; nothing gates it. */
  requires_paid_plan: boolean;
}

/** GET /api/communities/:communityId/courses/:courseId/lessons/:lessonId */
export interface CommunityLessonResponse {
  data: CommunityLesson;
}

/** POST …/lessons/:lessonId/complete — when the reader first marked it complete. */
export interface CommunityLessonCompletionResponse {
  data: { lesson_id: string; completed_at: string };
}

// =============================================================================
// RESPONSE: authoring (the community's admins)
// =============================================================================

/** A course as its authors manage it, a draft or published. */
export interface CommunityCourseDraft {
  course_id: string;
  title: string;
  description: string;
  position: number;
  published: boolean;
  created_at: string;
  updated_at: string;
}

/** A module as its authors manage it. */
export interface CommunityModuleDraft {
  module_id: string;
  course_id: string;
  title: string;
  position: number;
  created_at: string;
  updated_at: string;
}

/** A lesson as a course's authoring outline lists it: its title and state, no text. */
export interface CommunityLessonDraftListing {
  lesson_id: string;
  module_id: string;
  title: string;
  position: number;
  published: boolean;
  requires_paid_plan: boolean;
  /** The people who completed it (a count; no one's id). */
  completions_count: number;
}

/** A lesson, whole, as its authors edit it. */
export interface CommunityLessonDraft extends CommunityLesson {
  position: number;
  published: boolean;
  completions_count: number;
  created_at: string;
  updated_at: string;
}

/** A course's authoring outline: every module, and every lesson, drafts included, by position. */
export interface CommunityCourseDraftOutline {
  course: CommunityCourseDraft;
  modules: Array<CommunityModuleDraft & { lessons: CommunityLessonDraftListing[] }>;
}

/**
 * A lesson as a course's authoring export carries it: whole, as its authors edit it, its text as
 * written (`body`) and never the tree its clients draw; less its course (the export's own), its
 * completions and its creation.
 */
export type CommunityLessonExport = Omit<CommunityLessonDraft, 'body_tree' | 'course_id' | 'completions_count' | 'created_at'>;

/**
 * A course's authoring export (gh #5003): the course, every module and every lesson whole, drafts
 * included, by position, in one read. Its lessons carry no tree, so a course at the caps fits one
 * answer.
 */
export interface CommunityCourseExport {
  course: CommunityCourseDraft;
  modules: Array<CommunityModuleDraft & { lessons: CommunityLessonExport[] }>;
}

/** GET /api/communities/:communityId/authoring/courses — every course, drafts included, by position. */
export interface CommunityCourseDraftsResponse {
  data: CommunityCourseDraft[];
}

/** POST …/authoring/courses — 201; PATCH …/authoring/courses/:courseId — 200. */
export interface CommunityCourseDraftResponse {
  data: CommunityCourseDraft;
}

/** GET …/authoring/courses/:courseId */
export interface CommunityCourseDraftOutlineResponse {
  data: CommunityCourseDraftOutline;
}

/** GET …/authoring/courses/:courseId/export */
export interface CommunityCourseExportResponse {
  data: CommunityCourseExport;
}

/** POST …/modules — 201; PATCH …/modules/:moduleId — 200. */
export interface CommunityModuleDraftResponse {
  data: CommunityModuleDraft;
}

/** POST …/lessons — 201; GET and PATCH …/lessons/:lessonId — 200. */
export interface CommunityLessonDraftResponse {
  data: CommunityLessonDraft;
}

// =============================================================================
// STATS (Decision 28: course completions per lesson)
// =============================================================================

/** A lesson's completions: the people who marked it complete, once each (no one's id). */
export interface CommunityLessonStats {
  lesson_id: string;
  module_id: string;
  title: string;
  published: boolean;
  completions: number;
}

/** A course's lessons with their completions, drafts included, by module and lesson position. */
export interface CommunityCourseStats {
  course_id: string;
  title: string;
  published: boolean;
  lessons: CommunityLessonStats[];
}

// =============================================================================
// PROGRESS
// =============================================================================

/**
 * The reader's next lesson in a course (Design → Courses): of its published lessons, by position
 * (`lessonIds`, the outline's order), the first after the last one they completed; the first when
 * they completed none; null when they completed the last, or the course has none.
 */
export function communityNextLessonId(lessonIds: readonly string[], completed: { has(lessonId: string): boolean }): string | null {
  let last = -1;
  lessonIds.forEach((lessonId, i) => {
    if (completed.has(lessonId)) last = i;
  });
  return lessonIds[last + 1] ?? null;
}

/** An outline's lessons, by position: modules in order, each one's lessons in order. */
export function communityOutlineLessonIds(outline: { modules: ReadonlyArray<{ lessons: ReadonlyArray<{ lesson_id: string }> }> }): string[] {
  return outline.modules.flatMap((module) => module.lessons.map((lesson) => lesson.lesson_id));
}
